/**
 * Client-side storage for the "Sign in with Ledger" OAuth token pair
 * (ADR-030, docs/security-personal-apps.md §5).
 *
 * Every function is async even though today's implementation is a
 * synchronous `localStorage` read/write — that keeps this the single seam a
 * future Capacitor secure-storage adapter (see
 * docs/adr/20260515_03_mobile-app-strategy.md) swaps into without touching
 * any call site in `oauth_pkce.ts` or `auth_context.tsx`.
 *
 * That seam is still UNUSED inside the Capacitor shell
 * (`apps/wagepro-mobile`), which persists its token pair through this same
 * `localStorage` path. A WebView's `localStorage` is app-private and does
 * survive app restarts, so mobile sign-in is durable — but it is NOT the
 * OS-sandboxed store `docs/security-rules.md` sanctions for a native bearer
 * token (`@capacitor/preferences` → `UserDefaults`/`SharedPreferences`).
 * Swapping it in is deliberately NOT part of the backend-wiring pass,
 * because it runs into a real gap between two of this repo's own rules:
 * `scripts/lint-token-storage.sh` exempts `apps/*-mobile` on the assumption
 * that native token storage lives in the shell, while CLAUDE.md §"Mobile App
 * Architecture" (and `scripts/check-mobile-shell-purity.ts`) forbids the
 * shell from containing ANY source — so the sanctioned native write has
 * nowhere to live that the guard recognises. Resolving that (widen the
 * guard's native-platform-guarded exemption, vs. a tracked
 * `TOKEN_STORAGE_OK(#N)` acceptance) is a security-guardrail decision in its
 * own right, not a side effect of pointing the shell at the API.
 *
 * The storage adapter is an injectable parameter (defaulted to the browser's
 * `localStorage`) rather than a hardcoded global, so this module — and
 * everything built on top of it — is unit-testable in `bun:test` without a
 * DOM/jsdom dependency (wagepro-web has neither today).
 */

/** The token pair persisted between page loads. */
export interface StoredTokenPair {
  accessToken: string;
  refreshToken: string;
  /** Epoch milliseconds when the access token expires. */
  expiresAt: number;
}

/** Minimal storage surface this module needs — satisfied by `Storage` (localStorage/sessionStorage). */
export interface TokenStorageAdapter {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

// Bumped v1 -> v2 alongside #21929 PR4's scope widening (identity ->
// identity + payroll:employer:read/write + a resource indicator). A v1 token
// is identity-only with a null audience and cannot reach any
// payroll:employer:*-gated procedure, so it's not worth writing a migration
// for -- bumping the key forces one clean re-login instead.
//
// Bumped v2 -> v3 alongside C7 PR B's (#21938) `payroll:self:read` scope
// addition. Unlike the v1->v2 bump, staying on a stale v2 pair here fails
// CLOSED but OPAQUELY rather than cleanly: a v2 token lacks
// `payroll:self:read`, so `oauthFailClosedMiddleware` (mounted ahead of
// `requireAal2` on every OAuth-bearer procedure) rejects it with a bare
// FORBIDDEN carrying `aal2Required: null` AND `ledgerCode: null` -- a shape
// that satisfies NEITHER `isAal2Required` nor `isStepUpRequired` (see
// `step_up.test.ts`'s "generic global-gate rejection" case). Worse,
// `getValidToken`'s refresh path keeps successfully rotating that same
// scope-deficient pair indefinitely (a refresh grant never re-evaluates
// scopes), so the token never expires into `unauthenticated` and `AuthGate`
// never auto-fires `beginSignIn()` to fix it -- every OTHER screen keeps
// working, and My Pay shows a generic, seemingly-permanent `{ kind: 'error'
// }` whose `retry()` re-fires the same doomed token forever. Bumping the key
// sidesteps all of that: no v3 entry -> `unauthenticated` -> `AuthGate`'s
// existing auto-`beginSignIn()` -> the user re-consents (unavoidable anyway,
// since the scope set widened) -> a fresh 4-scope token.
const STORAGE_KEY = 'wagepro.auth.tokens.v3';

/**
 * Orphaned pre-current-version keys, superseded by {@link STORAGE_KEY}
 * above. Never read for token values -- kept only as literals for
 * {@link removeOrphanedTokens} to remove. Generalized from PR4's
 * single-key `V1_STORAGE_KEY`/`removeOrphanedV1Token` (renamed here) to a
 * list so each future version bump is a one-line addition, not a
 * find-and-rename of every reference.
 */
const ORPHANED_STORAGE_KEYS = [
  'wagepro.auth.tokens.v1',
  'wagepro.auth.tokens.v2',
] as const;

/**
 * Removes every orphaned key in {@link ORPHANED_STORAGE_KEYS} from `adapter`
 * if present. Not a migration -- an orphaned pair is never read forward into
 * the current version, it is simply deleted so it doesn't linger in storage
 * indefinitely. Pure and adapter-driven (no `window` reference) so it's
 * directly unit-testable with a fake adapter — see
 * {@link cleanupOrphanedTokensOnLoad} for the real-browser call site.
 */
export function removeOrphanedTokens(adapter: TokenStorageAdapter): void {
  for (const key of ORPHANED_STORAGE_KEYS) {
    if (adapter.getItem(key) !== null) {
      adapter.removeItem(key);
    }
  }
}

/**
 * One-time best-effort cleanup of every orphaned storage key, run once at
 * module-evaluation time against the REAL `window.localStorage` (guarded by
 * a `typeof window` check so this never touches anything inside
 * `bun:test`, which has no DOM). Wrapped in try/catch to match this
 * module's existing best-effort error-handling style — never throws, even
 * if storage access itself throws (e.g. a `SecurityError` under strict
 * browser privacy modes).
 *
 * Deliberately NOT threaded through {@link getStoredTokens}'s injectable
 * `adapter` parameter: every exported function here accepts an arbitrary
 * test adapter, and several call sites elsewhere
 * (`oauth_pkce.test.ts`'s refresh-dedup tests) use strict test doubles
 * whose `removeItem` throws to assert "this code path must not clear
 * storage" — wiring a cleanup call into the hot `getStoredTokens` path
 * would trip those unrelated assertions on every call. Running it once
 * here, only against the browser global, keeps it out of that hot path
 * entirely.
 */
function cleanupOrphanedTokensOnLoad(): void {
  if (typeof window === 'undefined') return;
  try {
    removeOrphanedTokens(window.localStorage);
  } catch {
    // Best-effort — storage access can throw under strict privacy modes.
  }
}
cleanupOrphanedTokensOnLoad();

/**
 * Lazily resolves the browser's `localStorage`. Only ever invoked when a
 * caller omits the `adapter` parameter, so importing this module in a
 * non-browser test environment never touches `window`.
 */
function browserAdapter(): TokenStorageAdapter {
  return window.localStorage;
}

function isStoredTokenPair(value: unknown): value is StoredTokenPair {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.accessToken === 'string' &&
    typeof candidate.refreshToken === 'string' &&
    typeof candidate.expiresAt === 'number'
  );
}

/**
 * Read the stored token pair, or `null` if absent/corrupt.
 *
 * Not declared `async` (there is nothing to `await` in today's synchronous
 * localStorage implementation — `@typescript-eslint/require-await` flags an
 * `async` function with no `await`) — it still returns a `Promise` via
 * `Promise.resolve`, preserving the async call-site contract every caller
 * (and a future Capacitor secure-storage adapter) relies on.
 */
export function getStoredTokens(
  adapter: TokenStorageAdapter = browserAdapter(),
): Promise<StoredTokenPair | null> {
  const raw = adapter.getItem(STORAGE_KEY);
  if (!raw) return Promise.resolve(null);
  try {
    const parsed: unknown = JSON.parse(raw);
    return Promise.resolve(isStoredTokenPair(parsed) ? parsed : null);
  } catch {
    return Promise.resolve(null);
  }
}

/** Persist the token pair, overwriting any prior value. */
export function setStoredTokens(
  tokens: StoredTokenPair,
  adapter: TokenStorageAdapter = browserAdapter(),
): Promise<void> {
  adapter.setItem(STORAGE_KEY, JSON.stringify(tokens));
  return Promise.resolve();
}

/** Remove any stored token pair. */
export function clearStoredTokens(
  adapter: TokenStorageAdapter = browserAdapter(),
): Promise<void> {
  adapter.removeItem(STORAGE_KEY);
  return Promise.resolve();
}
